/** V12-C — Tâches du jour : sources réelles, registre partagé avec le Commando (daily_plans + commando_action_log). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lightning, FunnelSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { TYPES } from "@/components/taches/TaskPrimitives";
import { TaskCard, DoneCard } from "@/components/taches/TaskCard";
import { PromotedBanner } from "@/components/taches/PromotedBanner";

const TABS = [["today", "Aujourd'hui"], ["late", "En retard"], ["upcoming", "À venir"], ["done", "Effectuées"]];

export default function Taches() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("today");
  const [typeF, setTypeF] = useState("");

  const load = useCallback(async () => {
    try { setData((await api.get("/tasks/today")).data); } catch { toast.error("Chargement des tâches impossible"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(typeof okMsg === "function" ? okMsg(r.data) : okMsg); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); } finally { setBusy(false); }
  };
  const onDone = (t) => act(() => api.post("/tasks/complete", { task_id: t.task_id }), (d) => (d.already_done ? "Déjà effectuée" : "Tâche effectuée"));
  const onSnooze = (t, until) => act(() => api.post("/tasks/snooze", { task_id: t.task_id, until }), `Reportée au ${until.split("-").reverse().join("/")}`);
  const onCancelSnooze = (t) => act(() => api.delete(`/tasks/snooze/${encodeURIComponent(t.task_id)}`), "Report annulé");
  const onUndo = (l) => act(() => api.post("/tasks/undo", { task_id: l.action_id }), (d) => (d.removed === 0 ? "Pointage déjà retiré" : d.crm_effect_kept ? "Pointage retiré — l'échéance CRM a déjà été recalculée, la tâche ne revient pas" : "Tâche rouverte"));

  const lists = useMemo(() => {
    const items = (data?.items || []).filter((t) => !typeF || t.type === typeF);
    return { today: items.filter((t) => t.bucket === "today"), late: items.filter((t) => t.bucket === "late"), upcoming: items.filter((t) => t.bucket === "upcoming"), done: (data?.done || []).filter((l) => !typeF || l.type === typeF) };
  }, [data, typeF]);

  return (
    <Layout>
      <div className="space-y-4" data-testid="taches-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="overline">Opérationnel</div>
            <h1 className="text-2xl font-extrabold text-slate-900">Tâches du jour</h1>
            <p className="text-[13px] text-slate-500">Actions secondaires centralisées — un seul état (à faire / effectué / reporté), partagé avec le Commando. Les 3 priorités et les rappels P1 restent dans le Commando.{data?.today && <span className="ml-1 text-slate-400" data-testid="taches-today-date">· {data.today.split("-").reverse().join("/")} (Europe/Paris)</span>}</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-secondary h-9 inline-flex items-center gap-1.5 text-[13px]" onClick={load} data-testid="taches-refresh"><ArrowsClockwise size={14} /> Actualiser</button>
            <Link to="/" className="btn-secondary h-9 inline-flex items-center gap-1.5 text-[13px]" data-testid="taches-to-commando"><Lightning size={14} weight="fill" /> Commando (3 priorités)</Link>
          </div>
        </div>
        <PromotedBanner items={data?.promoted} />
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5" data-testid="taches-tabs">
            {TABS.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`taches-tab-${k}`} className={`px-3 h-9 rounded-lg text-[13px] font-bold transition-colors ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {l} <span className={`ml-1 text-[11px] ${tab === k ? "text-slate-300" : "text-slate-400"}`} data-testid={`taches-count-${k}`}>{lists[k].length}</span>
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 text-[12px] text-slate-500"><FunnelSimple size={14} />
            <select className="input-base h-9 text-[13px]" value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="taches-filter-type">
              <option value="">Tous les types</option>{Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <div className="space-y-2" data-testid={`taches-list-${tab}`}>
          {loading && <div className="card-base p-8 text-center text-[13px] text-slate-400" data-testid="taches-loading">Chargement…</div>}
          {!loading && lists[tab].length === 0 && <div className="card-base p-8 text-center text-[13px] text-slate-400" data-testid="taches-empty">Rien ici — tout est à jour.</div>}
          {tab === "done"
            ? lists.done.map((l) => <DoneCard key={l.action_id + l.done_at} l={l} busy={busy} onUndo={onUndo} />)
            : lists[tab].map((t) => <TaskCard key={t.task_id} t={t} today={data.today} busy={busy} onDone={onDone} onSnooze={onSnooze} onCancelSnooze={onCancelSnooze} />)}
        </div>
        {data?.notes && <div className="text-[11px] text-slate-400" data-testid="taches-notes">{data.notes.join(" · ")}</div>}
      </div>
    </Layout>
  );
}
